import { useCallback, useState, type ReactNode } from 'react';
import { ConfirmDialog, type ConfirmRequest } from './ConfirmDialog';

/** What one open question resolves with: whether it was answered, and with what. */
type Answer = (yes: boolean, value: string) => void;

/**
 * The state behind both hooks: at most one question open, and never a caller
 * left waiting. A second question while one is open answers the first with
 * "no" instead of losing its promise.
 */
function useDialog(): [
  (request: ConfirmRequest) => Promise<{ yes: boolean; value: string }>,
  ReactNode,
] {
  const [pending, setPending] = useState<{ request: ConfirmRequest; resolve: Answer } | null>(null);

  const ask = useCallback(
    (request: ConfirmRequest) =>
      new Promise<{ yes: boolean; value: string }>((resolve) => {
        setPending((open) => {
          open?.resolve(false, '');
          return { request, resolve: (yes, value) => resolve({ yes, value }) };
        });
      }),
    [],
  );

  const answer = useCallback<Answer>((yes, value) => {
    setPending((open) => {
      open?.resolve(yes, value);
      return null;
    });
  }, []);

  const dialog = pending ? <ConfirmDialog request={pending.request} onAnswer={answer} /> : null;

  return [ask, dialog];
}

/**
 * An `await`-able confirmation and the element that asks it. Returns
 * `[confirm, dialog]`: render `dialog` anywhere in the component, and
 * `await confirm({...})` resolves true or false when the reader answers.
 */
export function useConfirm(): [(request: ConfirmRequest) => Promise<boolean>, ReactNode] {
  const [ask, dialog] = useDialog();
  const confirm = useCallback((request: ConfirmRequest) => ask(request).then((a) => a.yes), [ask]);
  return [confirm, dialog];
}

/**
 * The same dialog, asking for a value: `await prompt({...})` resolves to what
 * was typed, or null if the reader cancelled. The replacement for
 * `window.prompt`, which blocked the renderer exactly as `window.confirm` did
 * and could neither say what makes an answer acceptable nor refuse a bad one —
 * the admin's password reset typed a short password into it and learned that
 * from the server.
 *
 * `field.minLength` is what the answering button waits for; the platform still
 * checks it (`AccountService.MIN_PASSWORD_LENGTH`), because a client is never
 * the one that decides.
 */
export function usePrompt(): [(request: ConfirmRequest) => Promise<string | null>, ReactNode] {
  const [ask, dialog] = useDialog();
  const prompt = useCallback(
    (request: ConfirmRequest) => ask(request).then((a) => (a.yes ? a.value : null)),
    [ask],
  );
  return [prompt, dialog];
}
